import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  LinearProgress,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  SmartToy as AgentIcon,
  Add as AddIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Search as SearchIcon,
  AutoAwesome as AIIcon,
  TrendingUp as TrendingUpIcon,
  People as LeadsIcon,
  School as SchoolIcon,
  Web as WebIcon,
  DataObject as DataIcon,
  CheckCircle as ActiveIcon,
  Pause as PausedIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  NotificationsActive as NotificationIcon,
  CloudDownload as DownloadIcon,
  Link as LinkIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import { crmColors } from "../theme/colors";

const AIAgents = () => {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [newAgent, setNewAgent] = useState({
    name: "",
    type: "Lead Generation",
    description: "",
    enabled: true,
    schedule: "Daily",
    targetKeywords: "",
    searchRegions: "",
    maxResults: 50,
  });

  // Mock AI Agents data
  const aiAgents = [
    {
      id: 1,
      name: "Lead Generation Agent",
      type: "Lead Generation",
      description: "Automatically searches and identifies potential school leads from web sources",
      status: "Active",
      enabled: true,
      lastRun: "2024-01-15 10:30 AM",
      nextRun: "2024-01-16 10:00 AM",
      schedule: "Daily",
      leadsFound: 245,
      leadsConverted: 28,
      conversionRate: 11.4,
      successRate: 95,
      avgLeadsPerDay: 12,
      targetKeywords: ["school management software", "school ERP", "education management"],
      searchRegions: ["India", "Tier-1 Cities", "Tier-2 Cities"],
      maxResults: 50,
      sources: ["Google", "LinkedIn", "Education Portals", "Government Databases"],
      aiScore: 92,
    },
    {
      id: 2,
      name: "Data Mining Agent",
      type: "Data Mining",
      description: "Extracts and analyzes school data from public sources and databases",
      status: "Active",
      enabled: true,
      lastRun: "2024-01-15 9:00 AM",
      nextRun: "2024-01-16 9:00 AM",
      schedule: "Daily",
      dataPointsExtracted: 1250,
      schoolsProfiled: 89,
      accuracy: 94,
      successRate: 98,
      avgDataPerDay: 65,
      targetKeywords: ["school directory", "education institutions", "schools list"],
      searchRegions: ["All India"],
      maxResults: 100,
      sources: ["Government Portals", "Education Boards", "School Directories"],
      aiScore: 88,
    },
    {
      id: 3,
      name: "Web Profile Search Agent",
      type: "Web Search",
      description: "Searches and profiles schools from web sources, social media, and directories",
      status: "Active",
      enabled: true,
      lastRun: "2024-01-15 11:15 AM",
      nextRun: "2024-01-15 3:00 PM",
      schedule: "Twice Daily",
      profilesFound: 156,
      profilesVerified: 142,
      verificationRate: 91,
      successRate: 96,
      avgProfilesPerDay: 8,
      targetKeywords: ["school website", "school profile", "institution profile"],
      searchRegions: ["India"],
      maxResults: 30,
      sources: ["School Websites", "Social Media", "Education Portals"],
      aiScore: 90,
    },
    {
      id: 4,
      name: "Service Profile Manager",
      type: "Service Management",
      description: "Monitors and updates service profiles of existing schools",
      status: "Paused",
      enabled: false,
      lastRun: "2024-01-14 5:00 PM",
      nextRun: "Manual",
      schedule: "Weekly",
      profilesUpdated: 45,
      issuesDetected: 3,
      successRate: 93,
      avgUpdatesPerWeek: 45,
      targetKeywords: ["service status", "school updates", "profile changes"],
      searchRegions: ["All Active Schools"],
      maxResults: 100,
      sources: ["Internal Database", "School Portals"],
      aiScore: 85,
    },
    {
      id: 5,
      name: "Competitor Analysis Agent",
      type: "Market Intelligence",
      description: "Monitors competitor activities and market trends",
      status: "Active",
      enabled: true,
      lastRun: "2024-01-15 2:00 PM",
      nextRun: "2024-01-16 2:00 PM",
      schedule: "Daily",
      insightsGenerated: 78,
      competitorActivities: 12,
      marketTrends: 5,
      successRate: 89,
      avgInsightsPerDay: 4,
      targetKeywords: ["competitor", "market trends", "education technology"],
      searchRegions: ["Global"],
      maxResults: 25,
      sources: ["News", "Social Media", "Industry Reports"],
      aiScore: 87,
    },
  ];

  const agentTypes = [
    {
      value: "Lead Generation",
      label: "Lead Generation",
      icon: <LeadsIcon />,
      color: crmColors.primary.main,
      description: "Automatically find and qualify new school leads",
    },
    {
      value: "Data Mining",
      label: "Data Mining",
      icon: <DataIcon />,
      color: crmColors.primary.dark,
      description: "Extract school data from various sources",
    },
    {
      value: "Web Search",
      label: "Web Profile Search",
      icon: <WebIcon />,
      color: "#f59e0b",
      description: "Search and profile schools from web sources",
    },
    {
      value: "Service Management",
      label: "Service Profile Manager",
      icon: <SchoolIcon />,
      color: "#8b5cf6",
      description: "Monitor and manage service profiles",
    },
    {
      value: "Market Intelligence",
      label: "Market Intelligence",
      icon: <AnalyticsIcon />,
      color: "#ef4444",
      description: "Analyze market trends and competitors",
    },
  ];

  const recentLeads = [
    {
      id: 1,
      schoolName: "Bright Future School",
      location: "Mumbai, Maharashtra",
      phone: "+91 98765 43210",
      email: "info@brightfuture.edu",
      source: "Web Search Agent",
      foundDate: "2024-01-15",
      status: "New Lead",
      score: 85,
      website: "https://brightfuture.edu",
    },
    {
      id: 2,
      schoolName: "Excel Academy",
      location: "Delhi, NCR",
      phone: "+91 98765 43211",
      email: "contact@excel.edu",
      source: "Data Mining Agent",
      foundDate: "2024-01-15",
      status: "Qualified",
      score: 72,
      website: "https://excel.edu",
    },
    {
      id: 3,
      schoolName: "Knowledge Hub",
      location: "Bangalore, Karnataka",
      phone: "+91 98765 43212",
      email: "admin@knowledgehub.edu",
      source: "Lead Generation Agent",
      foundDate: "2024-01-14",
      status: "New Lead",
      score: 68,
      website: "https://knowledgehub.edu",
    },
  ];

  const getAgentTypeIcon = (type) => {
    const agentType = agentTypes.find((at) => at.value === type);
    return agentType ? agentType.icon : <AgentIcon />;
  };

  const getAgentTypeColor = (type) => {
    const agentType = agentTypes.find((at) => at.value === type);
    return agentType ? agentType.color : theme.palette.primary.main;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Paused":
        return "warning";
      case "Stopped":
        return "error";
      default:
        return "default";
    }
  };

  const handleToggleAgent = (agentId) => {
    toast.success("Agent status updated!");
  };

  const handleCreateAgent = () => {
    if (!newAgent.name.trim() || !newAgent.type) {
      toast.error("Please fill all required fields");
      return;
    }
    toast.success("AI Agent created successfully!");
    setAgentDialogOpen(false);
    setNewAgent({
      name: "",
      type: "Lead Generation",
      description: "",
      enabled: true,
      schedule: "Daily",
      targetKeywords: "",
      searchRegions: "",
      maxResults: 50,
    });
  };

  const totalLeadsFound = aiAgents.reduce((sum, agent) => sum + (agent.leadsFound || 0), 0);
  const totalDataPoints = aiAgents.reduce((sum, agent) => sum + (agent.dataPointsExtracted || 0), 0);
  const activeAgents = aiAgents.filter((a) => a.status === "Active").length;
  const avgSuccessRate = Math.round(aiAgents.reduce((sum, a) => sum + a.successRate, 0) / aiAgents.length);

  return (
    <PageContentLayout
      title="AI Agents for Lead Generation & Data Mining"
      subtitle="Automated AI agents to find leads, mine data, and search school profiles on the web"
      barTitle="AI Agents"
      breadcrumbs={[
        { label: "AI Agents", icon: <AgentIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
      actions={
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAgentDialogOpen(true)} sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
          Create AI Agent
        </Button>
      }
    >
        {/* Key Metrics */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Active Agents
                  </Typography>
                  <AgentIcon sx={{ color: theme.palette.primary.main, fontSize: "1.25rem" }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {activeAgents}/{aiAgents.length}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  {avgSuccessRate}% Avg Success Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Leads Found
                  </Typography>
                  <LeadsIcon sx={{ color: theme.palette.success.main, fontSize: "1.25rem" }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  {totalLeadsFound}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  {recentLeads.length} New Today
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Data Points
                  </Typography>
                  <DataIcon sx={{ color: theme.palette.info.main, fontSize: "1.25rem" }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                  {totalDataPoints.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  {aiAgents.find((a) => a.type === "Data Mining")?.schoolsProfiled || 0} Schools Profiled
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Conversion Rate
                  </Typography>
                  <TrendingUpIcon sx={{ color: theme.palette.warning.main, fontSize: "1.25rem" }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                  {aiAgents.find((a) => a.type === "Lead Generation")?.conversionRate || 0}%
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  From AI-Generated Leads
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card sx={{ mb: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="AI Agents" />
            <Tab label="Recent Leads" />
            <Tab label="Data Mining Results" />
            <Tab label="Agent Analytics" />
          </Tabs>
        </Card>

        {/* AI Agents Tab */}
        {selectedTab === 0 && (
          <Grid container spacing={2}>
            {aiAgents.map((agent) => (
              <Grid item xs={12} md={6} key={agent.id}>
                <Card
                  sx={{
                    border: `2px solid ${agent.enabled ? getAgentTypeColor(agent.type) : "divider"}`,
                    "&:hover": {
                      boxShadow: 4,
                      transform: "translateY(-4px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(getAgentTypeColor(agent.type), 0.1),
                            color: getAgentTypeColor(agent.type),
                            width: 48,
                            height: 48,
                          }}
                        >
                          {getAgentTypeIcon(agent.type)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                            {agent.name}
                          </Typography>
                          <Chip
                            label={agent.status}
                            size="small"
                            color={getStatusColor(agent.status)}
                            sx={{ height: "18px", fontSize: "0.65rem", mt: 0.25 }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title={agent.enabled ? "Pause Agent" : "Start Agent"}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleAgent(agent.id)}
                            color={agent.enabled ? "warning" : "success"}
                          >
                            {agent.enabled ? <StopIcon fontSize="small" /> : <StartIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Configure">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedAgent(agent);
                              setConfigDialogOpen(true);
                            }}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, fontSize: "0.875rem" }}>
                      {agent.description}
                    </Typography>

                    {/* Agent Stats */}
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      {agent.leadsFound !== undefined && (
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                              Leads Found
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.primary.main }}>
                              {agent.leadsFound}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {agent.dataPointsExtracted !== undefined && (
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                              Data Points
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.success.main }}>
                              {agent.dataPointsExtracted}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {agent.profilesFound !== undefined && (
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                              Profiles Found
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.info.main }}>
                              {agent.profilesFound}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={agent.leadsFound !== undefined || agent.profilesFound !== undefined ? 6 : 12}>
                        <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                            Success Rate
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.warning.main }}>
                            {agent.successRate}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Agent Info */}
                    <Box sx={{ mb: 2, p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "block", mb: 0.5 }}>
                        Schedule: {agent.schedule} • Next Run: {agent.nextRun}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "block" }}>
                        Last Run: {agent.lastRun}
                      </Typography>
                    </Box>

                    {/* AI Score */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AIIcon sx={{ color: theme.palette.primary.main, fontSize: "1rem" }} />
                        <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                          AI Performance Score
                        </Typography>
                      </Box>
                      <Chip
                        label={agent.aiScore}
                        size="small"
                        color={agent.aiScore >= 90 ? "success" : agent.aiScore >= 80 ? "info" : "warning"}
                        sx={{ fontSize: "0.7rem", height: "20px" }}
                      />
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        startIcon={<AnalyticsIcon />}
                      >
                        Analytics
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        startIcon={<ViewIcon />}
                      >
                        View Results
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Recent Leads Tab */}
        {selectedTab === 1 && (
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search leads..."
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                  }}
                  sx={{ flexGrow: 1, minWidth: 250 }}
                />
                <Button variant="outlined" size="small" startIcon={<DownloadIcon />}>
                  Export Leads
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>School Name</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Lead Score</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Found Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentLeads.map((lead) => (
                      <TableRow key={lead.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                              {lead.schoolName}
                            </Typography>
                            {lead.website && (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                                <LinkIcon fontSize="small" sx={{ fontSize: "0.7rem", color: "text.secondary" }} />
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: "0.7rem", color: "primary.main", cursor: "pointer" }}
                                  onClick={() => window.open(lead.website, "_blank")}
                                >
                                  View Website
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <LocationIcon fontSize="small" sx={{ fontSize: "0.875rem", color: "text.secondary" }} />
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {lead.location}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: "0.875rem", display: "flex", alignItems: "center", gap: 0.5 }}>
                              <PhoneIcon fontSize="small" sx={{ fontSize: "0.75rem" }} />
                              {lead.phone}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}>
                              <EmailIcon fontSize="small" sx={{ fontSize: "0.7rem" }} />
                              {lead.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={lead.source}
                            size="small"
                            sx={{
                              fontSize: "0.7rem",
                              height: "22px",
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                              {lead.score}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={lead.score}
                              sx={{ width: "60px", height: 4, borderRadius: 2 }}
                              color={lead.score >= 80 ? "success" : lead.score >= 60 ? "warning" : "error"}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={lead.status}
                            size="small"
                            color={lead.status === "Qualified" ? "success" : "info"}
                            sx={{ fontSize: "0.7rem", height: "22px" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                            {lead.foundDate}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                            <IconButton size="small" color="primary">
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="primary">
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Data Mining Results Tab */}
        {selectedTab === 2 && (
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                Extracted School Data
              </Typography>
              <Grid container spacing={2}>
                {[1, 2, 3, 4].map((item) => (
                  <Grid item xs={12} md={6} key={item}>
                    <Card variant="outlined">
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <SchoolIcon sx={{ color: theme.palette.primary.main }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                              Sample School {item}
                            </Typography>
                          </Box>
                          <Chip
                            label="Verified"
                            size="small"
                            color="success"
                            sx={{ fontSize: "0.7rem", height: "20px" }}
                          />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                            Location: Mumbai, Maharashtra
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                            Students: 850
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                            Phone: +91 98765 43210
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                            Email: info@school.edu
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                            Website: https://school.edu
                          </Typography>
                        </Box>
                        <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                          <Button variant="outlined" size="small" fullWidth>
                            View Details
                          </Button>
                          <Button variant="contained" size="small" fullWidth>
                            Add to CRM
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Agent Analytics Tab */}
        {selectedTab === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                    Agent Performance
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {aiAgents.map((agent) => (
                      <Box key={agent.id}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                            {agent.name}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                            {agent.successRate}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={agent.successRate}
                          sx={{ height: 6, borderRadius: 3 }}
                          color={agent.successRate >= 90 ? "success" : agent.successRate >= 80 ? "info" : "warning"}
                        />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                    Daily Activity
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {aiAgents.filter((a) => a.avgLeadsPerDay || a.avgDataPerDay || a.avgProfilesPerDay).map((agent) => (
                      <Box
                        key={agent.id}
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: alpha(getAgentTypeColor(agent.type), 0.05),
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem", mb: 0.5 }}>
                          {agent.name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                          Avg: {agent.avgLeadsPerDay || agent.avgDataPerDay || agent.avgProfilesPerDay} per day
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Create Agent Dialog */}
        <Dialog open={agentDialogOpen} onClose={() => setAgentDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create AI Agent</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Agent Name"
                  value={newAgent.name}
                  onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                  size="small"
                  placeholder="e.g., Lead Generation Agent for Tier-2 Cities"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Agent Type</InputLabel>
                  <Select
                    value={newAgent.type}
                    label="Agent Type"
                    onChange={(e) => setNewAgent({ ...newAgent, type: e.target.value })}
                  >
                    {agentTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ color: type.color }}>{type.icon}</Box>
                          <Box>
                            <Typography variant="body2">{type.label}</Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                              {type.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={newAgent.description}
                  onChange={(e) => setNewAgent({ ...newAgent, description: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Schedule</InputLabel>
                  <Select
                    value={newAgent.schedule}
                    label="Schedule"
                    onChange={(e) => setNewAgent({ ...newAgent, schedule: e.target.value })}
                  >
                    <MenuItem value="Hourly">Hourly</MenuItem>
                    <MenuItem value="Twice Daily">Twice Daily</MenuItem>
                    <MenuItem value="Daily">Daily</MenuItem>
                    <MenuItem value="Weekly">Weekly</MenuItem>
                    <MenuItem value="Manual">Manual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Results per Run"
                  type="number"
                  value={newAgent.maxResults}
                  onChange={(e) => setNewAgent({ ...newAgent, maxResults: parseInt(e.target.value) })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Target Keywords (comma-separated)"
                  value={newAgent.targetKeywords}
                  onChange={(e) => setNewAgent({ ...newAgent, targetKeywords: e.target.value })}
                  size="small"
                  placeholder="school management, education ERP, school software"
                  helperText="Keywords the agent will search for"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Search Regions (comma-separated)"
                  value={newAgent.searchRegions}
                  onChange={(e) => setNewAgent({ ...newAgent, searchRegions: e.target.value })}
                  size="small"
                  placeholder="India, Mumbai, Delhi, Bangalore"
                  helperText="Geographic regions to focus search"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newAgent.enabled}
                      onChange={(e) => setNewAgent({ ...newAgent, enabled: e.target.checked })}
                    />
                  }
                  label="Enable Agent Immediately"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAgentDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateAgent}>
              Create Agent
            </Button>
          </DialogActions>
        </Dialog>

        {/* Configure Agent Dialog */}
        <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Configure Agent: {selectedAgent?.name}
          </DialogTitle>
          <DialogContent>
            {selectedAgent && (
              <Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
                      Target Keywords
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {selectedAgent.targetKeywords.split(",").map((keyword, idx) => (
                        <Chip key={idx} label={keyword.trim()} size="small" sx={{ fontSize: "0.7rem" }} />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
                      Search Regions
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {selectedAgent.searchRegions.split(",").map((region, idx) => (
                        <Chip key={idx} label={region.trim()} size="small" color="primary" sx={{ fontSize: "0.7rem" }} />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
                      Data Sources
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {selectedAgent.sources.map((source, idx) => (
                        <Chip key={idx} label={source} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Schedule</InputLabel>
                      <Select value={selectedAgent.schedule} label="Schedule">
                        <MenuItem value="Hourly">Hourly</MenuItem>
                        <MenuItem value="Twice Daily">Twice Daily</MenuItem>
                        <MenuItem value="Daily">Daily</MenuItem>
                        <MenuItem value="Weekly">Weekly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Max Results"
                      type="number"
                      value={selectedAgent.maxResults}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => { setConfigDialogOpen(false); toast.success("Agent configuration updated!"); }}>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
    </PageContentLayout>
  );
};

export default AIAgents;

